import { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import type { TaskStatus } from '@/types';

export interface DeleteStatusReassignDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  excludeStatusId: string;
  taskCount: number;
  statuses: TaskStatus[];
  isPending: boolean;
  onConfirm: (replacementStatusId: string) => Promise<void>;
}

export function DeleteStatusReassignDialog({
  open,
  onOpenChange,
  excludeStatusId,
  taskCount,
  statuses,
  isPending,
  onConfirm,
}: DeleteStatusReassignDialogProps) {
  const [replacementId, setReplacementId] = useState<string>('');

  const options = useMemo(() => {
    return [...statuses]
      .filter((s) => s.id !== excludeStatusId)
      .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
  }, [statuses, excludeStatusId]);

  useEffect(() => {
    if (open) {
      setReplacementId('');
    }
  }, [open, excludeStatusId]);

  const handleConfirm = async () => {
    if (!replacementId) return;
    await onConfirm(replacementId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Delete status</DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>
                You already have tasks with this status. What status would you like to give these tasks?
              </p>
              {taskCount > 0 ? (
                <p className="text-foreground/90">
                  This affects <strong>{taskCount}</strong> task{taskCount === 1 ? '' : 's'}.
                </p>
              ) : null}
            </div>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <Label htmlFor="replacement-status">Move existing tasks to</Label>
          {options.length === 0 ? (
            <p className="text-sm text-destructive">
              There is no other status to move tasks to. Add another status first, then try again.
            </p>
          ) : (
            <Select value={replacementId || undefined} onValueChange={setReplacementId}>
              <SelectTrigger id="replacement-status" className="w-full">
                <SelectValue placeholder="Select a status" />
              </SelectTrigger>
              <SelectContent>
                {options.map((s) => (
                  <SelectItem key={s.id} value={s.id}>
                    {s.name}
                    {s.isDoneState ? ' (done)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancel
          </Button>
          <Button type="button" onClick={handleConfirm} disabled={!replacementId || isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Move tasks and delete status
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
